<template>
  <NuxtLayout name="charter-destination">
    <div class="charter-destination">
      <div class="z-[1]">
        <LazyHotelInfoTab
          v-if="activeTab === 'info'"
          :hotel-info="hotelInfo"
        />
        <LazyCharterHotelFacilities
          v-if="activeTab === 'facilities'"
          class="p-0"
          :hotel-info="hotelInfo"
          :title="t('hotelFacilitiesTitle')"
          :show-icon-facilities="true"
          :is-expanded="true"
          :show-hotel-map-box="true"
        />
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup>
const { $sentryCaptureException } = useNuxtApp()
const charterHotelStore = useCharterHotelStore()
const dealfinderStore = useDealfinderStore()
const localeStore = useLocaleStore()
const rootStore = useRootStore()
const charterStore = useCharterStore()
const route = useRoute()
const router = useRouter()

const { featuredImg } = useCharterHotelMixin()
const { imageSize } = useHotelImagesMixin()
const { urls: localeURLs } = useLocale()
const { t } = useI18n()

const { error: fetchError, data } = await useAsyncData(async () => {
  try {
    const [{ data: result }] = await Promise.all([
      apiFetch(`/${localeStore.locale}/hotel/${encodeURIComponent(route.params.id)}`),
      rootStore.fetchCharterAirports(),
    ])

    const activeTab = findActiveSlugKey(localeURLs.hotelId, route.params)
    charterHotelStore.SET_CHARTER_HOTEL_TAB(activeTab)

    const hotelInfo = processHotelInfo(result)

    let destinationInfo
    if (hotelInfo.destination) {
      destinationInfo = await charterStore.fetchDestination({ destination: hotelInfo.destination.destination_page_id })
    }

    const secs = deepClone(destinationInfo?.sections || [])
    const weather = secs.find(s => s.type === 'weather')
    if (weather) {
      charterHotelStore.SET_CHARTER_HOTEL_WEATHER(weather)
    }

    dealfinderStore.SET_STOPOVER_LIST(hotelInfo.stopOvers)
    dealfinderStore.SET_HOTEL(hotelInfo)

    rootStore.SET_PAGE_BANNER({
      type: hotelInfo.hotelImage.type,
      background: {
        type: hotelInfo.hotelImage.type,
        image: {
          url: hotelInfo.hotelImage.url,
        },
      },
    })
    return {
      hotelInfo,
      destinationInfo,
    }
  } catch (e) {
    $sentryCaptureException(e)
    throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
  }
})

if (fetchError.value) {
  navigateTo(localeStore.urls.hotel)
}

const { hotelInfo } = data.value

const activeTab = computed(() => charterHotelStore.charterHotelTab)

const ogImage = featuredImg(hotelInfo)

useHead({
  title: () => `${hotelInfo.propertyName}, ${hotelInfo.townName}`,
  link: [
    {
      rel: 'canonical',
      href: () => `${localeURLs.BASE_URL}${localeURLs.hotel}/${hotelInfo.url_name}`,
    },
  ],

  meta: [
    { hid: 'author', name: 'author', content: () => t('siteTitleBrand') },
    {
      hid: 'og:url',
      property: 'og:url',
      content: () => `${localeURLs.BASE_URL}${localeURLs.hotel}/${hotelInfo.url_name}`,
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: () => `${hotelInfo.propertyName}, ${hotelInfo.townName} - ${t('siteTitleBrand')}`,
    },
    ...(!ogImage ? [] : [{
      hid: 'og:image',
      property: 'og:image',
      content: () => imageSize(ogImage, 'og', true),
    }]),
    {
      hid: 'og:description',
      name: 'og:description',
      content: () => hotelInfo.description,
    },
    {
      hid: 'description',
      name: 'description',
      content: () => trimByWord(hotelInfo.description),
    },
  ],
})

onMounted(() => {
  window.scrollTo(0, 0)
})
</script>
